'use client';

import { FC } from 'react';

import { PersonalAccountLayout } from '@app/layouts/PersonalAccountLayout';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { usePersonalAccountFields } from '@shared/hooks/usePersonalAccountFields';
import { FieldLabels } from '@shared/types/enums/fieldLabels.enum';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { VStack } from '@shared/ui/Stack';
import { validateEmail, validatePostalCode } from '@shared/utils/fieldValidators/fieldValidators';

import { addPersonalAccountInfoThunk, PersonalAccountAddressInfoType, PersonalAccountInfoType } from '../';
import cls from './PersonalAccount.module.scss';

type PersonalAccountProps = {
  profileInfo?: PersonalAccountInfoType;
  addressInfo?: PersonalAccountAddressInfoType;
};

export const PersonalAccount: FC<PersonalAccountProps> = ({ profileInfo, addressInfo }) => {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector((state) => state.personalAccountInfo.isLoading);

  const {
    email,
    phoneNumber,
    firstName,
    secondName,
    lastName,
    postalCode,
    region,
    city,
    street,
    houseNumber,
    corpus,
    building,
    apartment,
    floor,
    errorFirstName,
    errorLastName,
    errorPostalCode,
    errorEmail,
    handleChangeEmail,
    handleChangePhoneNumber,
    handleChangeLastName,
    handleChangeFirstName,
    handleChangeSecondName,
    handleChangePostalCode,
    handleChangeRegion,
    handleChangeCity,
    handleChangeStreet,
    handleChangeHouseNumber,
    handleChangeCorpus,
    handleChangeBuilding,
    handleChangeApartment,
    handleChangeFloor,
    setErrorLastName,
    setErrorFirstName,
    setErrorPostalCode,
    setErrorEmail
  } = usePersonalAccountFields({ addressInfo, profileInfo });

  const addPersonalInfo = () => {
    dispatch(
      addPersonalAccountInfoThunk({
        profileInfo: {
          firstName,
          secondName,
          lastName,
          account: {
            email
          }
        },
        addressInfo: {
          postalCode,
          region,
          city,
          street,
          house: houseNumber,
          corpus,
          building,
          apartment,
          floor
        }
      })
    );
  };

  const handleSave = () => {
    const validationPostalCode = validatePostalCode(postalCode);
    const validationEmail = validateEmail(email);

    if (!firstName || !lastName || !validationPostalCode || !validationEmail) {
      setErrorFirstName(!firstName);
      setErrorLastName(!lastName);
      setErrorPostalCode(!validationPostalCode);
      setErrorEmail(!validationEmail);
    } else {
      addPersonalInfo();
    }
  };

  return (
    <PersonalAccountLayout activePath={'profile'}>
      <VStack className={cls.form}>
        <VStack className={cls.section}>
          <h2 className={cls.title}>Мой профиль</h2>
          <div className={cls.emailAndPhoneNumberSection}>
            <VStack>
              <Input
                className={cls.input}
                label={FieldLabels.EMAIL}
                isError={errorEmail}
                adaptiveSize
                required
                value={email || ''}
                onChange={handleChangeEmail}
              />
            </VStack>
            <VStack>
              <Input
                className={cls.input}
                label={FieldLabels.PHONE_NUMBER}
                disabled={true}
                required
                adaptiveSize
                phoneMask
                value={phoneNumber || ''}
                onChange={handleChangePhoneNumber}
              />
            </VStack>
          </div>
        </VStack>

        <VStack className={cls.section}>
          <h2 className={cls.title}>Персональная информация</h2>
          <div className={cls.personalInfo}>
            <Input
              label={FieldLabels.LAST_NAME}
              isError={errorLastName}
              required
              adaptiveSize
              value={lastName || ''}
              onChange={handleChangeLastName}
            />
            <Input
              label={FieldLabels.FIRST_NAME}
              isError={errorFirstName}
              required
              adaptiveSize
              value={firstName || ''}
              onChange={handleChangeFirstName}
            />
            <Input
              label={FieldLabels.SECOND_NAME}
              adaptiveSize
              value={secondName || ''}
              onChange={handleChangeSecondName}
            />
          </div>
        </VStack>

        <VStack className={cls.section}>
          <h2 className={cls.title}>Адрес доставки</h2>
          <VStack className={cls.addressWrapper}>
            <Input
              label={FieldLabels.POSTAL_CODE}
              adaptiveSize
              isError={errorPostalCode}
              fullWidth={true}
              value={postalCode || ''}
              onChange={handleChangePostalCode}
            />
            <div className={cls.addressSecondSection}>
              <Input
                label={FieldLabels.REGION}
                adaptiveSize
                value={region || ''}
                onChange={handleChangeRegion}
              />
              <Input
                label={FieldLabels.CITY}
                adaptiveSize
                value={city || ''}
                onChange={handleChangeCity}
              />
              <Input
                label={FieldLabels.STREET}
                adaptiveSize
                value={street || ''}
                onChange={handleChangeStreet}
              />
            </div>
            <div className={cls.addressFirstSection}>
              <Input
                label={FieldLabels.HOUSE_NUMBER}
                inputMode={'numeric'}
                adaptiveSize
                value={houseNumber || ''}
                onChange={handleChangeHouseNumber}
              />
              <Input
                label={FieldLabels.CORPUS}
                inputMode={'numeric'}
                adaptiveSize
                value={corpus || ''}
                onChange={handleChangeCorpus}
              />
            </div>
            <div className={cls.addressSecondSection}>
              <Input
                inputMode={'numeric'}
                label={FieldLabels.BUILDING}
                adaptiveSize
                value={building || ''}
                onChange={handleChangeBuilding}
              />
              <Input
                label={FieldLabels.APARTMENT}
                inputMode={'numeric'}
                adaptiveSize
                value={apartment || ''}
                onChange={handleChangeApartment}
              />
              <Input
                label={FieldLabels.FLOOR}
                inputMode={'numeric'}
                adaptiveSize
                value={floor || ''}
                onChange={handleChangeFloor}
              />
            </div>
          </VStack>
        </VStack>
      </VStack>
      <Button
        className={cls.button}
        size={'l'}
        onClick={handleSave}
        isLoading={isLoading}
        loadingButton
      >
        Сохранить
      </Button>
    </PersonalAccountLayout>
  );
};
